import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AdminCrudPage, AdminCrudPageConfig } from '@/components/admin';
import api from '@/services/api';

interface Lookup {
  id: string;
  name: string;
}

type SubgroupRow = {
  id: string;
  name: string;
  code: string;
  description: string;
  group: string;
  group_name: string;
  type_name: string;
  is_active: boolean;
  products_count: number;
  [key: string]: unknown;
};

function SubgroupDetail({ item }: { item: SubgroupRow }) {
  return (
    <dl className="space-y-3 text-sm">
      <div>
        <dt className="font-medium text-gray-500 dark:text-gray-400">Name</dt>
        <dd className="text-gray-900 dark:text-white">{item.name}</dd>
      </div>
      <div>
        <dt className="font-medium text-gray-500 dark:text-gray-400">Code</dt>
        <dd className="text-gray-900 dark:text-white">{item.code}</dd>
      </div>
      <div>
        <dt className="font-medium text-gray-500 dark:text-gray-400">Group</dt>
        <dd className="text-gray-900 dark:text-white">
          {item.group_name || '-'} {item.type_name ? `(${item.type_name})` : ''}
        </dd>
      </div>
      <div>
        <dt className="font-medium text-gray-500 dark:text-gray-400">Description</dt>
        <dd className="whitespace-pre-wrap text-gray-900 dark:text-white">
          {item.description || 'No description.'}
        </dd>
      </div>
      <div>
        <dt className="font-medium text-gray-500 dark:text-gray-400">Status</dt>
        <dd className="text-gray-900 dark:text-white">{item.is_active ? 'Active' : 'Inactive'}</dd>
      </div>
      <div>
        <dt className="font-medium text-gray-500 dark:text-gray-400">Products</dt>
        <dd className="text-gray-900 dark:text-white">{item.products_count}</dd>
      </div>
    </dl>
  );
}

/**
 * Product Subgroups.
 *
 * Item 8 (2026-09-18 batch): a Group filter beside the search bar, a View
 * detail popup, and `hasActivate` -- `InsuranceSubgroup.is_active` exists on
 * the model and `InsuranceSubgroupViewSet.filterset_fields` already includes
 * both `group` and `is_active`, so all three are CONFIG on top of the
 * `AdminCrudPage` this page already used, not new backend work. The Group
 * filter's options are the one dynamic piece -- `FilterConfig.options` is a
 * plain array, so they are fetched once here and handed to a `config` built
 * inside the component rather than the module-level constant this page used
 * to export.
 */
export function SubgroupsPage() {
  const { data: groups = [] } = useQuery<Lookup[]>({
    queryKey: ['insurance-groups-lookup-for-subgroup-filter'],
    queryFn: async () => {
      const res = await api.get('/products/groups/', { params: { page_size: 200 } });
      return res.data.results || res.data;
    },
  });

  const config: AdminCrudPageConfig<SubgroupRow> = useMemo(
    () => ({
      title: 'Product Subgroups',
      apiEndpoint: '/products/subgroups/',
      // Backed by a viewset that mixes in `core.import_export.ImportExportMixin`,
      // so the export/import-template/import routes this turns on really exist.
      hasImportExport: true,
      queryKey: 'productSubgroups',
      // Widths turn on `AdminCrudPage`'s fixed table layout (G2) for this
      // screen; without them `tableLayout: 'fixed'` below would collapse every
      // column to an equal share, which is worse than the default.
      columns: [
        { key: 'name', label: 'Name', width: '20%', sortable: true },
        { key: 'group_name', label: 'Group', width: '20%' },
        { key: 'description', label: 'Description', width: '45%' },
        { key: 'is_active', label: 'Status', width: '10%', render: (v: unknown) => (v ? 'Active' : 'Inactive') },
      ],
      tableLayout: 'fixed',
      formFields: [
        { name: 'name', label: 'Name', type: 'text', required: true },
        { name: 'group', label: 'Group', type: 'select', apiEndpoint: '/products/groups/', required: true, searchable: true },
        { name: 'description', label: 'Description', type: 'text' },
        { name: 'is_active', label: 'Active', type: 'boolean' },
      ],
      filters: [
        {
          key: 'group',
          label: 'Group',
          options: groups.map((g) => ({ value: g.id, label: g.name })),
        },
      ],
      hasActivate: true,
      renderView: (item) => <SubgroupDetail item={item} />,
      viewTitle: (item) => item.name,
    }),
    [groups]
  );

  return <AdminCrudPage config={config} />;
}
